import { useQuery } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Section, StatCard, fmtBytes } from '@/components/db/parts';
import { Device } from '@/lib/device';
import { fetchMqttServiceInfo } from '@/api/endpoints';
import type { MqttServiceInfo } from '@/api/types';

function fmtUptime(s: number): string {
  if (!s) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function Mqtt({ device }: { device: Device }) {
  const { data: info, isLoading } = useQuery<MqttServiceInfo>({
    queryKey: ['mqtt', 'info'],
    queryFn: fetchMqttServiceInfo,
    refetchInterval: 5000,
  });

  const down = info && !info.reachable;

  return (
    <PageShell device={device} title="Service MQTT" breadcrumb="Services">
      <div className="p-4 md:p-6 space-y-4">

        {/* ── État (temps réel) ── */}
        <Section title="État (temps réel · 5 s)" action={
          info ? (
            <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${info.reachable && info.health.ok ? 'badge-green' : 'badge-slate'}`}>
              {info.reachable ? (info.health.ok ? 'up' : 'dégradé') : 'injoignable'}
            </span>
          ) : null
        }>
          {isLoading || !info ? (
            <div className="text-sm text-muted-foreground">Chargement…</div>
          ) : down ? (
            <div className="text-sm p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/40">
              ⚠ Service <strong>{info.configured ? 'injoignable' : 'non configuré'}</strong> —{' '}
              {info.configured
                ? 'le broker MQTT (ou son sidecar metrics) ne répond pas (réessai automatique).'
                : <>la variable d&apos;environnement <code className="font-mono">MQTT_CLIMATO_URL</code> n&apos;est pas définie côté admin.</>}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Clients connectés" value={info.broker.clientsConnected} sub={`${info.broker.subscriptions} abonnements`} tone="primary" />
                <StatCard label="Sidecar metrics" value={info.sidecar.connected ? 'connecté' : 'déconnecté'} tone={info.sidecar.connected ? 'success' : 'warn'} sub={`${info.sidecar.reconnects} reconnexions`} />
                <StatCard label="Messages reçus" value={info.broker.messagesReceived} sub={`${info.broker.dropped} dropped`} tone={info.broker.dropped > 0 ? 'warn' : 'default'} />
                <StatCard label="Uptime broker" value={fmtUptime(info.broker.uptimeS)} sub={`${info.broker.messagesStored} stockés`} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Octets reçus" value={fmtBytes(info.broker.bytesReceived)} />
                <StatCard label="Octets envoyés" value={fmtBytes(info.broker.bytesSent)} />
                <StatCard label="Clients actifs" value={info.broker.clientsActive} />
                <StatCard label="Clients déconnectés" value={info.broker.clientsDisconnected} />
              </div>

              {info.perStation.length > 0 && (
                <div className="overflow-x-auto">
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5">Messages par station</div>
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">station</th>
                        <th className="text-right px-3 py-2">messages</th>
                        <th className="text-right px-3 py-2">octets</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {info.perStation.map((p) => (
                        <tr key={p.station}>
                          <td className="px-3 py-2 font-mono text-xs">{p.station}</td>
                          <td className="px-3 py-2 font-mono text-xs text-right">{p.messages}</td>
                          <td className="px-3 py-2 font-mono text-xs text-right">{fmtBytes(p.bytes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ── Actions (none upstream) ── */}
        <Section title="Actions">
          <p className="text-sm text-muted-foreground">
            <code className="font-mono">traitement_mqtt</code> est un broker Mosquitto avec un sidecar metrics : il route les messages{' '}
            <code className="font-mono">climato/#</code> (les stations publient, node_climato consomme et écrit en base).
            Il n&apos;expose <strong>aucune action HTTP</strong> (<code className="font-mono">POST /refreshCache</code> est un no-op).
            Le cycle de vie (start/stop/restart) et la rotation des users MQTT passent par Clever Cloud.
          </p>
        </Section>

      </div>
    </PageShell>
  );
}
